import { useState, useCallback, useEffect } from 'react'
import { cn } from '../../lib/utils'

interface MobileKeyboardBarProps {
  onSendData: (data: string) => void
  visible: boolean
  keyboardHeight: number
}

interface KeyButton {
  label: string
  data: string | null  // null means it's a modifier
  isModifier?: boolean
  wide?: boolean
}

const KEYS: KeyButton[] = [
  { label: 'Tab', data: '\t' },
  { label: '⇧Tab', data: '\x1b[Z' },  // Shift+Tab
  { label: 'Esc', data: '\x1b' },
  { label: 'Ctrl', data: null, isModifier: true },
  { label: 'Ctrl+C', data: '\x03' },
  { label: '↑', data: '\x1b[A' },
  { label: '↓', data: '\x1b[B' },
  { label: '←', data: '\x1b[D' },
  { label: '→', data: '\x1b[C' },
  { label: 'PgUp', data: '\x15' },  // Ctrl+U = half-page up in vi copy-mode (single byte, escape-time safe)
  { label: 'PgDn', data: '\x04' },  // Ctrl+D = half-page down in vi copy-mode (single byte, escape-time safe)
  { label: 'Scroll', data: '\x02[' },  // Ctrl+b [ - enter tmux copy mode
]

/**
 * Mobile keyboard bar with extra terminal control keys.
 * Shows above the mobile keyboard when terminal is active.
 * Uses fixed positioning to stay above the keyboard.
 */
export function MobileKeyboardBar({ onSendData, visible, keyboardHeight }: MobileKeyboardBarProps) {
  const [ctrlActive, setCtrlActive] = useState(false)
  const [bottomOffset, setBottomOffset] = useState(0)

  // Track the visual viewport to position above keyboard
  useEffect(() => {
    if (!visible) return

    const updatePosition = () => {
      const viewport = window.visualViewport
      if (viewport) {
        // Position at the bottom of the visible viewport
        // The keyboard pushes the viewport up, so we use viewport.height + viewport.offsetTop
        const viewportBottom = window.innerHeight - (viewport.height + viewport.offsetTop)
        setBottomOffset(Math.max(viewportBottom, 0))
      } else {
        // Fallback to provided keyboard height
        setBottomOffset(keyboardHeight)
      }
    }

    updatePosition()

    const viewport = window.visualViewport
    if (viewport) {
      viewport.addEventListener('resize', updatePosition)
      viewport.addEventListener('scroll', updatePosition)
      return () => {
        viewport.removeEventListener('resize', updatePosition)
        viewport.removeEventListener('scroll', updatePosition)
      }
    }
  }, [visible, keyboardHeight])

  const handleKeyPress = useCallback((key: KeyButton) => {
    if (key.isModifier) {
      // Toggle Ctrl modifier
      setCtrlActive(prev => !prev)
      return
    }

    if (key.data) {
      onSendData(key.data)
    }
  }, [onSendData])

  // Handle regular key input when Ctrl is active
  const handleCtrlKey = useCallback((char: string) => {
    // Convert character to Ctrl code (A=1, B=2, etc.)
    const upperChar = char.toUpperCase()
    const charCode = upperChar.charCodeAt(0)

    if (charCode >= 65 && charCode <= 90) {
      // A-Z maps to 1-26
      const ctrlCode = charCode - 64
      onSendData(String.fromCharCode(ctrlCode))
    }

    setCtrlActive(false)
  }, [onSendData])

  if (!visible) return null

  return (
    <div
      className="fixed left-0 right-0 z-50 flex items-center gap-1.5 px-2 py-1.5 bg-card border-t border-border overflow-x-auto"
      style={{
        bottom: bottomOffset,
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {KEYS.map((key, index) => (
        <button
          key={index}
          onClick={() => handleKeyPress(key)}
          className={cn(
            'px-3 py-2 min-w-[44px] min-h-[44px] rounded-md text-sm font-medium transition-colors',
            'flex items-center justify-center flex-shrink-0',
            key.isModifier && ctrlActive
              ? 'bg-primary text-primary-foreground'
              : 'bg-secondary hover:bg-secondary/80 text-secondary-foreground',
            key.wide && 'min-w-[60px]'
          )}
        >
          {key.label}
        </button>
      ))}

      {/* Ctrl+letter quick access when Ctrl is active */}
      {ctrlActive && (
        <>
          <div className="w-px h-6 bg-border mx-1 flex-shrink-0" />
          {['A', 'D', 'L', 'Z'].map(char => (
            <button
              key={char}
              onClick={() => handleCtrlKey(char)}
              className={cn(
                'px-3 py-2 min-w-[44px] min-h-[44px] rounded-md text-sm font-medium transition-colors',
                'flex items-center justify-center flex-shrink-0',
                'bg-primary/20 hover:bg-primary/30 text-primary'
              )}
            >
              Ctrl+{char}
            </button>
          ))}
        </>
      )}
    </div>
  )
}
